/* eslint-disable check-file/filename-blocklist */
import { createElement } from 'react';
import type { IntlShape } from 'react-intl';

import type { ADNode } from '@atlaskit/editor-common/utils/validator';
import type { EditorActions } from '@atlaskit/editor-core/actions';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import type { ExtensionModule } from '@atlaskit/editor-common/extensions/extension-manifest';
import type { QuickInsertPreview } from '@atlaskit/editor-common/quick-insert/preview';
import { messages as quickInsertMessages } from '@atlaskit/editor-common/quick-insert/messages';
import { ConfluenceAttributionIcon } from '@atlaskit/editor-common/quick-insert/confluence-attribution-icon';
import { JiraAttributionIcon } from '@atlaskit/editor-common/quick-insert/jira-attribution-icon';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { ExtensionPlugin } from '@atlaskit/editor-plugins/extension';
import AppIcon from '@atlaskit/icon/core/app';
import AttachmentIcon from '@atlaskit/icon/core/attachment';
import DecisionIcon from '@atlaskit/icon/core/decision';
import RoadmapIcon from '@atlaskit/icon/core/roadmap';
import SearchIcon from '@atlaskit/icon/core/search';
import SmartLinkListIcon from '@atlaskit/icon/core/smart-link-list';
import TaskIcon from '@atlaskit/icon/core/task';
import TextIndentRightIcon from '@atlaskit/icon/core/text-indent-right';
import JiraIcon from '@atlaskit/icon-lab/core/jira';

import { isExperimentEnabled } from '@confluence/feature-experiments';
import { getMacroId } from '@confluence/macro-tracker/entry-points/getExtensionKey.ts';
import { cfetch } from '@confluence/network';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';

import type { ExtensionNodeType } from '../../extensions-common/extensionNodeTypes.ts';
import { isBodiedExtension } from '../../extensions-common/extensionNodeTypes.ts';
import { allowedMacrosForInsertMenu } from '../../extensionConstants.ts';
import {
	getMacroQuickInsertPreviewOverride,
	localizeQuickInsertPreview,
	type QuickInsertPreviewOverride,
} from '../../extensions-common/macroOverrides.ts';

import type { MacroConfig } from './extension-provider-types.ts';
import type {
	LegacyMacroManifest,
	ConfluencePageContext,
	OnCompleteCallbackFnType,
} from './extensionTypes.ts';
// oxlint-disable-next-line import/no-cycle
import { safeGetMacroName } from './manifest-helpers.tsx';
// eslint-disable-next-line import/no-cycle
import { getQuickInsertAction } from './quick-insert-action.ts';
// oxlint-disable-next-line import/no-cycle
import { getIconComponent } from './MacroComponents.tsx';

const pageIdRegex = /^[0-9]+$/;
const EMBED_CATEGORY = 'embed';
const IFRAME_MACRO = 'iframe';
const IFRAME_EMBED_PRIORITY = -200;
const mediaQuickInsertMacros = new Set(['roadmap', 'profile-picture', 'attachments']);
const appIconQuickInsertMacros = new Set([
	'include',
	'toc-zone',
	'opsgenie-incident-timeline-prod-jsm-issue-activity-macro',
	'opsgenie-incident-timeline-eu-jsm-issue-activity-macro',
	'opsgenie-incident-timeline-prod-macro',
	'opsgenie-incident-timeline-eu-macro',
	'viewpdf',
	'widget',
	'sharelinks-urlmacro',
	'jirachart',
]);
const otherQuickInsertMacros = new Set([
	'excerpt',
	'excerpt-include',
	'include',
	'jirachart',
	'jira',
	'portfolioforjiraplan',
	'opsgenie-incident-timeline-prod-jsm-issue-activity-macro',
	'opsgenie-incident-timeline-eu-jsm-issue-activity-macro',
	'opsgenie-incident-timeline-eu-macro',
	'opsgenie-incident-timeline-prod-macro',
	'sharelinks-urlmacro',
	'toc-zone',
	'viewpdf',
	'viewxls',
	'widget',
]);
const otherQuickInsertMacroPriorities: Readonly<Record<string, number>> = {
	excerpt: -4400,
	'excerpt-include': -4300,
	include: -4200,
	'toc-zone': -4100,
	jira: -4000,
	portfolioforjiraplan: -3900,
	'opsgenie-incident-timeline-prod-jsm-issue-activity-macro': -3800,
	'opsgenie-incident-timeline-eu-jsm-issue-activity-macro': -3700,
	'opsgenie-incident-timeline-prod-macro': -3600,
	'opsgenie-incident-timeline-eu-macro': -3500,
	viewxls: -3400,
	viewpdf: -3300,
	widget: -3200,
	'sharelinks-urlmacro': -3100,
	jirachart: -1900,
};
type GadgetQuickInsertMetadata = { priority: number; preview?: QuickInsertPreviewOverride };
const gadgetQuickInsertMetadata: Readonly<Record<string, GadgetQuickInsertMetadata>> = {
	'com.atlassian.streams.streams-jira-plugin:activitystream-gadget': {
		priority: -3000,
		preview: {
			attribution: {
				message: quickInsertMessages.previewAttributionJira,
				icon: JiraAttributionIcon,
			},
		},
	},
	'com.pyxis.greenhopper.jira:greenhopper-gadget-rapid-view': {
		priority: -2900,
		preview: {
			attribution: {
				message: quickInsertMessages.previewAttributionJira,
				icon: JiraAttributionIcon,
			},
		},
	},
	'com.atlassian.jira.gadgets:assigned-to-me-gadget': {
		priority: -2800,
		preview: {
			attribution: {
				message: quickInsertMessages.previewAttributionJira,
				icon: JiraAttributionIcon,
			},
			image: {
				dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVI0.png',
				light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVKT.png',
			},
		},
	},
	'com.atlassian.jira.gadgets:average-age-chart-gadget': {
		priority: -2700,
		preview: {
			attribution: {
				message: quickInsertMessages.previewAttributionJira,
				icon: JiraAttributionIcon,
			},
			image: {
				dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVK9.png',
				light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVN3.png',
			},
		},
	},
	'com.atlassian.jira.ext.charting:numberoftimesinstatus-gadget': {
		priority: -2600,
		preview: {
			attribution: {
				message: quickInsertMessages.previewAttributionJira,
				icon: JiraAttributionIcon,
			},
			image: {
				dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVK6.png',
				light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVN1.png',
			},
		},
	},
	'com.atlassian.jira.ext.charting:timeinstatus-gadget': { priority: -2500 },
	'com.atlassian.jira.gadgets:created-vs-resolved-issues-chart-gadget': { priority: -2400 },
	'com.pyxis.greenhopper.jira:greenhopper-gadget-sprint-days-remaining': {
		priority: -2300,
		preview: {
			attribution: {
				message: quickInsertMessages.previewAttributionJira,
				icon: JiraAttributionIcon,
			},
		},
	},
	'com.atlassian.jira.gadgets:filter-results-gadget': {
		priority: -2200,
		preview: {
			attribution: {
				message: quickInsertMessages.previewAttributionJira,
				icon: JiraAttributionIcon,
			},
			image: {
				dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVKC.png',
				light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVM7.png',
			},
		},
	},
	'com.atlassian.jira.gadgets:heat-map-gadget': {
		priority: -2100,
		preview: {
			attribution: {
				message: quickInsertMessages.previewAttributionJira,
				icon: JiraAttributionIcon,
			},
			image: {
				dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVIY.png',
				light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVLW.png',
			},
		},
	},
	'com.atlassian.jira.gadgets:in-progress-gadget': {
		priority: -2000,
		preview: {
			attribution: {
				message: quickInsertMessages.previewAttributionJira,
				icon: JiraAttributionIcon,
			},
		},
	},
	'com.atlassian.jira.ext.calendar:issuescalendar-gadget': {
		priority: -1800,
		preview: {
			attribution: {
				message: quickInsertMessages.previewAttributionJira,
				icon: JiraAttributionIcon,
			},
			image: {
				dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVJF.png',
				light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVKV.png',
			},
		},
	},
	'com.atlassian.jira.gadgets:road-map-gadget': {
		priority: -1700,
		preview: {
			attribution: {
				message: quickInsertMessages.previewAttributionJira,
				icon: JiraAttributionIcon,
			},
			image: {
				dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVK8.png',
				light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVL5.png',
			},
		},
	},
	'com.atlassian.jira.gadgets:labels-gadget': {
		priority: -1600,
		preview: {
			attribution: {
				message: quickInsertMessages.previewAttributionJira,
				icon: JiraAttributionIcon,
			},
		},
	},
	'com.atlassian.jira.gadgets:pie-chart-gadget': {
		priority: -1500,
		preview: {
			attribution: {
				message: quickInsertMessages.previewAttributionJira,
				icon: JiraAttributionIcon,
			},
		},
	},
	'com.atlassian.jira.gadgets:quicklinks-gadget': {
		priority: -1400,
		preview: {
			attribution: {
				message: quickInsertMessages.previewAttributionJira,
				icon: JiraAttributionIcon,
			},
		},
	},
	'com.atlassian.jira.gadgets:recently-created-chart-gadget': {
		priority: -1300,
		preview: {
			attribution: {
				message: quickInsertMessages.previewAttributionJira,
				icon: JiraAttributionIcon,
			},
		},
	},
	'com.atlassian.jira.gadgets:resolution-time-gadget': {
		priority: -1200,
		preview: {
			attribution: {
				message: quickInsertMessages.previewAttributionJira,
				icon: JiraAttributionIcon,
			},
		},
	},
	'com.pyxis.greenhopper.jira:greenhopper-gadget-sprint-burndown': {
		priority: -1100,
		preview: {
			attribution: {
				message: quickInsertMessages.previewAttributionJira,
				icon: JiraAttributionIcon,
			},
		},
	},
	'com.pyxis.greenhopper.jira:greenhopper-gadget-sprint-health': {
		priority: -1000,
		preview: {
			attribution: {
				message: quickInsertMessages.previewAttributionJira,
				icon: JiraAttributionIcon,
			},
		},
	},
	'com.atlassian.jira.gadgets:favourite-filters-gadget': { priority: -900 },
	'com.atlassian.jira.gadgets:time-since-chart-gadget': {
		priority: -800,
		preview: {
			attribution: {
				message: quickInsertMessages.previewAttributionJira,
				icon: JiraAttributionIcon,
			},
		},
	},
	'com.atlassian.jira.ext.charting:firstresponse-gadget': {
		priority: -700,
		preview: {
			attribution: {
				message: quickInsertMessages.previewAttributionJira,
				icon: JiraAttributionIcon,
			},
		},
	},
	'com.atlassian.jira.gadgets:two-dimensional-stats-gadget': {
		priority: -600,
		preview: {
			attribution: {
				message: quickInsertMessages.previewAttributionJira,
				icon: JiraAttributionIcon,
			},
		},
	},
	'com.pyxis.greenhopper.jira:greenhopper-gadget-version-report': {
		priority: -500,
		preview: {
			attribution: {
				message: quickInsertMessages.previewAttributionJira,
				icon: JiraAttributionIcon,
			},
		},
	},
	'com.atlassian.jira.gadgets:voted-gadget': {
		priority: -400,
		preview: {
			attribution: {
				message: quickInsertMessages.previewAttributionJira,
				icon: JiraAttributionIcon,
			},
		},
	},
	'com.atlassian.jira.gadgets:watched-gadget': {
		priority: -300,
		preview: {
			attribution: {
				message: quickInsertMessages.previewAttributionJira,
				icon: JiraAttributionIcon,
			},
		},
	},
	'com.atlassian.jira.gadgets:stats-gadget': {
		priority: -200,
		preview: {
			attribution: {
				message: quickInsertMessages.previewAttributionJira,
				icon: JiraAttributionIcon,
			},
		},
	},
	'com.atlassian.jira.ext.charting:workloadpie-gadget': {
		priority: -100,
		preview: {
			attribution: {
				message: quickInsertMessages.previewAttributionJira,
				icon: JiraAttributionIcon,
			},
		},
	},
};
const mediaQuickInsertMacroPriorities: Readonly<Record<string, number>> = {
	roadmap: -400,
	'profile-picture': -300,
	attachments: -200,
};
const gadgetModulePathPrefix = '/rest/gadgets/1.0/g/';

const getGadgetQuickInsertMetadata = (
	macro: LegacyMacroManifest,
): GadgetQuickInsertMetadata | undefined => {
	const gadgetUrl = macro.formDetails.parameters.find(
		(parameter) => parameter.name === 'url',
	)?.defaultValue;
	const gadgetModulePath = gadgetUrl?.split(gadgetModulePathPrefix)[1];
	const gadgetModuleKey = gadgetModulePath?.split('/')[0];

	return gadgetModuleKey ? gadgetQuickInsertMetadata[gadgetModuleKey] : undefined;
};

const getGadgetQuickInsertPriority = (macro: LegacyMacroManifest): number | undefined =>
	getGadgetQuickInsertMetadata(macro)?.priority;

const getGadgetQuickInsertPreview = (
	macro: LegacyMacroManifest,
	intl: IntlShape,
): QuickInsertPreview | undefined => {
	const preview = getGadgetQuickInsertMetadata(macro)?.preview;
	return preview ? localizeQuickInsertPreview(preview, intl) : undefined;
};

const dataAndChartsQuickInsertMacroPriorities: Readonly<Record<string, number>> = {
	children: -900,
	livesearch: -800,
	'recently-updated': -700,
	details: -600,
	detailssummary: -500,
	contentbylabel: -400,
	pagetree: -300,
	pagetreesearch: -200,
	'blog-posts': -100,
	'content-report-table': 0,
	'tasks-report-macro': 100,
	decisionreport: 200,
	contributors: 300,
	'change-history': 400,
	profile: 500,
	userlister: 600,
	listlabels: 700,
	'popular-labels': 800,
	spaces: 900,
	calendar: 1000,
	'questionslist-macro-native': 1100,
};

const isSpacesGadget = (macro: LegacyMacroManifest): boolean =>
	macro.macroName === 'gadget' && macro.title === 'Spaces';

const isDataAndChartsAppMacro = (macro: LegacyMacroManifest): boolean =>
	macro.categories.some((category) => {
		const normalizedCategory = category.trim().toLowerCase();
		return normalizedCategory === 'reporting' || normalizedCategory === 'development';
	});

const getQuickInsertIcon = (macro: LegacyMacroManifest, macroName: string) => {
	if (isExperimentEnabled('platform_editor_slash_command')) {
		if (macro.macroName === 'gadget' || appIconQuickInsertMacros.has(macroName)) {
			return () => createElement(AppIcon, { label: '' });
		}

		switch (macroName) {
			case 'attachments':
				return () => createElement(AttachmentIcon, { label: '' });
			case 'children':
				return () => createElement(SmartLinkListIcon, { label: '' });
			case 'livesearch':
				return () => createElement(SearchIcon, { label: '' });
			case 'roadmap':
				return () => createElement(RoadmapIcon, { label: '' });
			case 'tasks-report-macro':
				return () => createElement(TaskIcon, { label: '' });
			case 'decisionreport':
				return () => createElement(DecisionIcon, { label: '' });
			case 'spaces':
			case 'calendar':
				return () => createElement(AppIcon, { label: '' });
			case 'excerpt':
			case 'excerpt-include':
				return () => createElement(TextIndentRightIcon, { label: '' });
			case 'portfolioforjiraplan':
				return () => createElement(JiraIcon, { label: '' });
		}
	}

	return () => getIconComponent(macro);
};

export const isExternalContentAppMacro = (macro: LegacyMacroManifest): boolean =>
	Boolean(getConnectAppKey(macro.moduleKey)) && macro.categories.includes('external-content');
export const isPageId = (id: string): boolean => pageIdRegex.test(id);

// Temporary solution until we have `extensionAPI.doc.getSelectedNode` @see ED-13045
export const getSelectedBodiedExtensionNode = (
	editorActions?: EditorActions,
): ADNode | undefined => {
	const editorView = editorActions?._privateGetEditorView();
	if (editorView) {
		let maybeNode = (editorView.state.selection as any).node;
		if (!maybeNode || !isBodiedExtension(maybeNode.type.name)) {
			const { $head } = editorView.state.selection;
			// If current selection isn't bodied extension traverse upward
			for (let i = $head.depth; i > 0; i--) {
				const node = $head.node(i);
				if (isBodiedExtension(node.type.name as ExtensionNodeType)) {
					maybeNode = node;
				}
			}
		}
		return maybeNode?.toJSON() as ADNode;
	}
};

export const isValueSet = (value: any): boolean =>
	(value === 0 || value === false || !!value) && (!Array.isArray(value) || value.length > 0);

/**
 * True when a Team Calendar macro instance has a configured calendar id.
 *
 * Handles both the wrapped ({ value }) and bare macroParams shapes, and
 * type-guards against non-string runtime values (macroParams values are typed
 * `any` and are not guaranteed to be strings) before calling `.trim()`.
 */
export const hasConfiguredTeamCalendar = (
	macroParams?: Record<string, { value?: unknown } | string | undefined>,
): boolean => {
	const id = macroParams?.id;
	const value = typeof id === 'object' && id !== null && 'value' in id ? id.value : id;

	return typeof value === 'string' && value.trim() !== '';
};

export const createQuickInsertModule = (
	macro: LegacyMacroManifest,
	pageContext: ConfluencePageContext,
	openMacroBrowserForInitialConfiguration: any,
	editorAPI: PublicPluginAPI<[ExtensionPlugin]> | undefined,
	intl: IntlShape,
	macroBrowserConfig?: MacroConfig,
	editorActions?: EditorActions,
	createAnalyticsEvent?: CreateUIAnalyticsEvent,
	onCompleteCallback?: OnCompleteCallbackFnType,
	isLivePage?: boolean,
	externalContentAppMacroPriority?: number,
): ExtensionModule[] => {
	// Hidden macros don't appear in the slash menu
	// but still need to be defined in a manifest so old pages can show them
	if (macro.hidden) {
		return [];
	}

	const macroName = safeGetMacroName(macro);
	const isEmbedQuickInsertMacro = macroName === IFRAME_MACRO || isExternalContentAppMacro(macro);
	const isOtherQuickInsertMacro =
		(macro.macroName === 'gadget' && !isSpacesGadget(macro)) ||
		otherQuickInsertMacros.has(macroName);
	const dataAndChartsPriority =
		dataAndChartsQuickInsertMacroPriorities[macroName] ?? (isSpacesGadget(macro) ? 900 : undefined);
	const category = !isExperimentEnabled('platform_editor_slash_command')
		? undefined
		: isEmbedQuickInsertMacro
			? EMBED_CATEGORY
			: isOtherQuickInsertMacro
				? 'other'
				: mediaQuickInsertMacros.has(macroName)
					? 'media'
					: dataAndChartsPriority !== undefined || isDataAndChartsAppMacro(macro)
						? 'data-and-charts'
						: undefined;
	const priority = !isExperimentEnabled('platform_editor_slash_command')
		? undefined
		: macroName === IFRAME_MACRO
			? IFRAME_EMBED_PRIORITY
			: isEmbedQuickInsertMacro
				? externalContentAppMacroPriority
				: isOtherQuickInsertMacro
					? macro.macroName === 'gadget'
						? getGadgetQuickInsertPriority(macro)
						: otherQuickInsertMacroPriorities[macroName]
					: (mediaQuickInsertMacroPriorities[macroName] ?? dataAndChartsPriority);
	const icon = getQuickInsertIcon(macro, macroName);
	const preview =
		macro.macroName === 'gadget'
			? (getGadgetQuickInsertPreview(macro, intl) ??
				(isSpacesGadget(macro)
					? localizeQuickInsertPreview(
							{
								attribution: {
									message: quickInsertMessages.previewAttributionConfluence,
									icon: ConfluenceAttributionIcon,
								},
							},
							intl,
						)
					: undefined))
			: getMacroQuickInsertPreviewOverride(macro.macroName, intl);

	try {
		const action = getQuickInsertAction(
			macro,
			pageContext,
			openMacroBrowserForInitialConfiguration,
			editorAPI,
			intl,
			macroBrowserConfig,
			editorActions,
			createAnalyticsEvent,
			onCompleteCallback,
			isLivePage,
		);

		return [
			{
				key: macroName,
				title: macro.title,
				description: macro.description,
				keywords: [macro.macroName, ...macro.aliases],
				...(category ? { category } : {}),
				...(priority !== undefined ? { priority } : {}),
				...(preview ? { preview } : {}),
				categories: macro.categories,
				icon: async () => icon,
				featured:
					pageContext.features.elementBrowser && allowedMacrosForInsertMenu.includes(macroName),
				action,
			},
		];
	} catch (e) {
		// eslint-disable-next-line no-console
		console.error(e);
		return [];
	}
};

// Extract App Key from moduleKey for connect app
// com.atlassian.plugins.atlassian-connect-plugin:xhtml__cc-eco-test-connect-multi-bodied-extension__cc-eco-test-multi-bodied-extension
// ====>
// cc-eco-test-connect-multi-bodied-extension
export const getConnectAppKey = (macroKey?: string | null): string => {
	if (!macroKey || !macroKey.startsWith('com.atlassian.plugins.atlassian-connect-plugin')) {
		return '';
	}

	const chunks = macroKey.split('__');
	if (chunks.length !== 3) {
		return '';
	}

	return chunks[1];
};

export const insertMacroPlaceholder = (node: any, placeholderText: string): void => {
	node.content = [
		{
			type: 'paragraph',
			content: [
				{
					type: 'placeholder',
					attrs: {
						text: placeholderText,
					},
				},
			],
		},
	];
};

export const insertContentPropertiesMacroPlaceholder = (
	node: any,
	keyPlaceholderHeading: string,
	valuePlaceholderHeading: string,
): void => {
	node.content = [
		{
			type: 'table',
			attrs: {
				isNumberColumnEnabled: false,
				layout: 'default',
				localId: getMacroId(null),
				width: 760,
			},
			content: [
				{
					type: 'tableRow',
					content: [
						{
							type: 'tableHeader',
							attrs: {},
							content: [
								{
									type: 'paragraph',
									content: [
										{
											type: 'placeholder',
											attrs: {
												text: keyPlaceholderHeading,
											},
										},
									],
								},
							],
						},
						{
							type: 'tableCell',
							attrs: {
								background: '#ffffff',
							},
							content: [
								{
									type: 'paragraph',
									content: [
										{
											type: 'placeholder',
											attrs: {
												text: valuePlaceholderHeading,
											},
										},
									],
								},
							],
						},
					],
				},
				{
					type: 'tableRow',
					content: [
						{
							type: 'tableHeader',
							attrs: {
								background: '#f4f5f7',
							},
							content: [
								{
									type: 'paragraph',
									content: [],
								},
							],
						},
						{
							type: 'tableCell',
							attrs: {},
							content: [
								{
									type: 'paragraph',
									content: [],
								},
							],
						},
					],
				},
				{
					type: 'tableRow',
					content: [
						{
							type: 'tableHeader',
							attrs: {
								background: '#f4f5f7',
							},
							content: [
								{
									type: 'paragraph',
									content: [],
								},
							],
						},
						{
							type: 'tableCell',
							attrs: {},
							content: [
								{
									type: 'paragraph',
									content: [],
								},
							],
						},
					],
				},
			],
		},
	];
};

const getMatchingAppLinkForViewPage = async (selectedServerId: string) => {
	try {
		const res = await cfetch('/wiki/rest/jiraanywhere/1.0/servers');
		const appLinks: { url: string; rpcUrl: string; id: string }[] = await res.json();

		return appLinks.find((appLink) => appLink.id === selectedServerId);
	} catch {}
};

const isCloudAppLinkFromUrl = (url?: string) => {
	if (!url) {
		return;
	}

	return url.includes('atlassian.net') || url.includes('jira-dev.com');
};

export const getJiraHostingOption = async (
	macroName: string,
	selectedServerId: string = '',
	isViewPage: boolean = false,
): Promise<'unknown' | 'cloud' | 'server' | undefined> => {
	if (macroName !== 'jira') {
		return;
	}

	let isCloudAppLink: boolean | undefined;

	if (isViewPage) {
		const selectedServerObject = await getMatchingAppLinkForViewPage(selectedServerId);

		isCloudAppLink =
			isCloudAppLinkFromUrl(selectedServerObject?.url) ||
			isCloudAppLinkFromUrl(selectedServerObject?.rpcUrl);
	} else {
		try {
			// module is only available on edit page
			const ApplinksBridge = window.require('confluence/jim/jira/applinks-bridge-for-spa');

			isCloudAppLink = await ApplinksBridge.isCloudAppLinkFromServerId(selectedServerId);
		} catch {}
	}

	if (isCloudAppLink === undefined) {
		return 'unknown';
	}

	return isCloudAppLink ? 'cloud' : 'server';
};

// This is not an extensive list, as some macros are added elsewhere (ex. Smart button)
export const MACROS_TO_HIDE_COMPANY_HUB: Set<string> = new Set([
	'anchor',
	'attachments',
	'calendar',
	'children',
	'contentbylabel',
	'content-report-table',
	'create-from-template',
	'decisionreport',
	'detailssummary',
	'gadget',
	'excerpt',
	'excerpt-include',
	'expand',
	'iframe',
	'include',
	'jira',
	'listlabels',
	'pagetree',
	'pagetreesearch',
	'panel',
	'popular-labels',
	'profile',
	'recently-updated-dashboard',
	'roadmap',
	'spaces',
	'tasks-report-macro',
	'toc',
	'toc-zone',
	'userlister',
	'viewpdf',
	'viewxls',
]);

export const filterOutHiddenMacros = (
	macros: any,
	hideMacrosOptions?: { isLivePage?: boolean; isCompanyHub?: boolean },
): any[] => {
	const newMacros: any[] = [];
	const macrosToHideLivePage = new Set(
		StatsigConfigurations.getStringList('confluence_frontend_hide_selected_macros', []),
	);

	for (const macro of macros) {
		const hideCompanyHubMacro =
			hideMacrosOptions?.isCompanyHub && MACROS_TO_HIDE_COMPANY_HUB.has(macro?.macroName);
		const hideLivePageMacro =
			hideMacrosOptions?.isLivePage && macrosToHideLivePage.has(macro?.macroName);

		if (hideCompanyHubMacro || hideLivePageMacro) {
			const hiddenMacro = { ...macro, hidden: true };
			newMacros.push(hiddenMacro);
		} else {
			newMacros.push(macro);
		}
	}
	return newMacros;
};
